import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../lib/supabaseClient';
import Spinner from '../Atoms/Spinner';

// ─── Styled Components ───────────────────────────────────────
const PageContainer = styled.div`
    max-width: 720px;
    margin: 0 auto;
    padding: 40px 20px 80px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const LogoImg = styled.img`
    display: block;
    margin: 0 auto 20px;
    max-width: 180px;
`;

const PageTitle = styled.h1`
    text-align: center;
    font-size: 1.6rem;
    color: #1a1a2e;
    margin-bottom: 8px;
    font-weight: 700;
`;

const PageSubtitle = styled.p`
    text-align: center;
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 32px;
`;

const DesignerBadge = styled.div`
    text-align: center;
    font-size: 1.1rem;
    font-weight: 600;
    color: rgb(24, 51, 189);
    background: #f0f4ff;
    border: 1px solid #d0d8ff;
    border-radius: 8px;
    padding: 12px 20px;
    margin-bottom: 28px;
`;

const SectionCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    padding: 24px;
    margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
    font-size: 1.1rem;
    color: rgb(24, 51, 189);
    margin-bottom: 20px;
    font-weight: 700;
    border-bottom: 2px solid rgb(24, 51, 189);
    padding-bottom: 8px;
`;

const DownloadLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, rgb(24, 51, 189), rgb(18, 40, 155));
    color: white;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.2s;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,51,189,0.3); }
`;

const DropZone = styled.div`
    border: 2px dashed ${props => props.hasFile ? '#27ae60' : '#d0d0d0'};
    background: ${props => props.hasFile ? '#f0fff4' : '#fafafa'};
    border-radius: 10px;
    padding: 32px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 16px;
    &:hover { border-color: ${props => props.hasFile ? '#27ae60' : 'rgb(24, 51, 189)'}; background: ${props => props.hasFile ? '#e8f5e9' : '#f0f4ff'}; }
`;

const DropZoneLabel = styled.div`
    font-size: 14px;
    color: ${props => props.hasFile ? '#27ae60' : '#888'};
    font-weight: ${props => props.hasFile ? '600' : '400'};
`;

const SubmitBtn = styled.button`
    display: block;
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #27ae60, #229954);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(39,174,96,0.3); }
    &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const Alert = styled.div`
    padding: 14px 20px;
    border-radius: 8px;
    font-size: 14px;
    margin-bottom: 20px;
    background: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#fff3cd'};
    color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#856404'};
    border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#ffeeba'};
`;

const NotFound = styled.div`
    text-align: center;
    padding: 60px 20px;
    h2 { font-size: 1.4rem; color: #333; margin-bottom: 12px; }
    p { color: #666; }
`;

const AlreadyUploaded = styled.div`
    padding: 16px;
    background: #d4edda;
    border: 1px solid #c3e6cb;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

// ─── Component ───────────────────────────────────────────────
const FD100ExpoContratForm = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [piece, setPiece] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) { setNotFound(true); setLoading(false); return; }
        const load = async () => {
            const { data, error } = await supabase
                .from('fd100_expo_finale_pieces')
                .select('*')
                .eq('token', token)
                .single();
            if (error || !data) {
                setNotFound(true);
            } else {
                setPiece(data);
            }
            setLoading(false);
        };
        load();
    }, [token]);

    const ACCEPTED_TYPES = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    ];

    const handleFileSelect = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!ACCEPTED_TYPES.includes(f.type)) {
            setError('Seuls les fichiers PDF et DOCX sont acceptés.');
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            setError('Le fichier ne doit pas dépasser 10 Mo.');
            return;
        }
        setFile(f);
        setError(null);
    };

    const handleUpload = async () => {
        if (!file || !piece) return;
        setSubmitting(true);
        setError(null);

        try {
            const ext = file.name.split('.').pop().toLowerCase() || 'pdf';
            const fileName = `fd100-contrats-signes/${token}_${Date.now()}.${ext}`;
            const contentType = file.type === 'application/pdf' ? 'application/pdf' : 'application/octet-stream';
            const { error: upErr } = await supabase.storage
                .from('fd100-documents')
                .upload(fileName, file, { contentType, upsert: true });

            if (upErr) {
                setError('Erreur lors de l\'upload: ' + upErr.message);
                setSubmitting(false);
                return;
            }

            const { data: { publicUrl } } = supabase.storage
                .from('fd100-documents')
                .getPublicUrl(fileName);

            const { error: updateErr } = await supabase
                .from('fd100_expo_finale_pieces')
                .update({
                    contrat_signe_url: publicUrl,
                    contrat_signe_at: new Date().toISOString(),
                })
                .eq('id', piece.id);

            if (updateErr) {
                setError('Erreur lors de la sauvegarde.');
                setSubmitting(false);
                return;
            }

            setPiece(prev => ({ ...prev, contrat_signe_url: publicUrl, contrat_signe_at: new Date().toISOString() }));
            setSubmitted(true);
            setFile(null);
        } catch (err) {
            setError('Erreur inattendue: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spinner />
                </div>
            </PageContainer>
        );
    }

    if (notFound) {
        return (
            <PageContainer>
                <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
                <NotFound>
                    <h2>Page introuvable</h2>
                    <p>Le lien utilisé est invalide ou a expiré.</p>
                </NotFound>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <LogoImg src="/img/logo.png" alt="Le FRENCH DESIGN" />
            <PageTitle>Contrat de prêt</PageTitle>
            <PageSubtitle>Exposition « Hospitalité lyrique, l'émotion dans l'art de recevoir »</PageSubtitle>

            <DesignerBadge>
                {piece.nom_piece}
                {piece.designer_names && ` — ${piece.designer_names}`}
            </DesignerBadge>

            {error && <Alert type="error">{error}</Alert>}
            {submitted && <Alert type="success">Contrat signé envoyé avec succès. Merci !</Alert>}

            {/* Download contract section */}
            <SectionCard>
                <SectionTitle>Contrat à télécharger</SectionTitle>
                {piece.contrat_admin_url ? (
                    <div>
                        <p style={{ fontSize: 14, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
                            Veuillez télécharger votre contrat de prêt ci-dessous, le signer, puis uploader la version signée dans la section suivante.
                        </p>
                        <DownloadLink href={piece.contrat_admin_url} target="_blank" rel="noreferrer">
                            Télécharger le contrat (PDF)
                        </DownloadLink>
                    </div>
                ) : (
                    <Alert type="warning">
                        Le contrat n'est pas encore disponible. Vous serez notifié dès qu'il sera prêt.
                    </Alert>
                )}
            </SectionCard>

            {/* Upload signed contract section */}
            <SectionCard>
                <SectionTitle>Upload du contrat signé</SectionTitle>

                {piece.contrat_signe_url && !submitted && (
                    <AlreadyUploaded>
                        <div>
                            <strong style={{ color: '#155724' }}>Contrat signé déjà envoyé</strong>
                            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                                {piece.contrat_signe_at && `Envoyé le ${new Date(piece.contrat_signe_at).toLocaleDateString('fr-FR')}`}
                            </div>
                        </div>
                        <a href={piece.contrat_signe_url} target="_blank" rel="noreferrer"
                            style={{ color: '#155724', fontWeight: 600, fontSize: 13 }}>
                            Voir le PDF
                        </a>
                    </AlreadyUploaded>
                )}

                <p style={{ fontSize: 14, color: '#555', marginBottom: 16, lineHeight: 1.6 }}>
                    {piece.contrat_signe_url
                        ? 'Vous pouvez remplacer le contrat signé en uploadant une nouvelle version.'
                        : 'Uploadez le contrat signé au format PDF ou DOCX (10 Mo maximum).'}
                </p>

                <DropZone
                    hasFile={!!file}
                    onClick={() => document.getElementById('contrat-file-input').click()}
                >
                    <input
                        id="contrat-file-input"
                        type="file"
                        accept="application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <DropZoneLabel hasFile={!!file}>
                        {file ? file.name : 'Cliquez pour sélectionner un fichier PDF ou DOCX'}
                    </DropZoneLabel>
                    {!file && (
                        <div style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>PDF ou DOCX — 10 Mo max</div>
                    )}
                </DropZone>

                <SubmitBtn
                    onClick={handleUpload}
                    disabled={!file || submitting}
                >
                    {submitting ? 'Envoi en cours...' : (piece.contrat_signe_url ? 'Remplacer le contrat signé' : 'Envoyer le contrat signé')}
                </SubmitBtn>
            </SectionCard>
        </PageContainer>
    );
};

export default FD100ExpoContratForm;
